import { Autocomplete, Box, Button, Checkbox, TablePagination, TextField, Tooltip, Typography } from "@mui/material";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useNavigate } from "react-router-dom";
import { useState } from "react";
import { useMutation, useQuery } from "react-query";
import moment from "moment";
import useTitle from "hooks/useTitle";
import { ResType } from "types";
import { getAdminActivityLog, getAdminActivityLogStats, exportAdminActivityLog } from "api/services/adminActivityLog";
import { getUsers } from "api/services/users";
import { snack } from "components/toast";
import { handleError } from "utils/handleError";
import { getCurrentFormattedDateTime } from "utils";
import DateRange from "views/dashboard/OrgDashboard/DateRange";
import excelIcon from "./../../../assets/images/128px-Microsoft_Office_Excel_(2019–2025).svg.png";

const MODULE_OPTIONS = [
    { value: "", label: "All Modules" },
    { value: "attendance", label: "Attendance" },
    { value: "shift", label: "Shifts" },
    { value: "preferences", label: "Preferences" },
];

const MODULE_LABEL: Record<string, string> = {
    attendance: "Attendance",
    shift: "Shift",
    preferences: "Preferences",
};

// Colors keyed by action - green for approvals, red for rejections/deletions, orange for
// edits/overrides, blue for everything else (creates, check-in/out, assignments, settings).
const ACTION_STYLE: Record<string, { color: string; label: string }> = {
    CREATED: { color: "#2563eb", label: "Added" },
    UPDATED: { color: "#d97706", label: "Updated" },
    DELETED: { color: "#dc2626", label: "Deleted" },
    CHECK_IN: { color: "#2563eb", label: "Checked In" },
    CHECK_OUT: { color: "#2563eb", label: "Checked Out" },
    LEAVE_APPLIED: { color: "#7c3aed", label: "Leave Applied" },
    APPROVED: { color: "#15803d", label: "Approved" },
    REJECTED: { color: "#dc2626", label: "Rejected" },
    ASSIGNED: { color: "#2563eb", label: "Assigned" },
    SET_DEFAULT: { color: "#2563eb", label: "Set Default" },
};

// Field names as they're actually stored (raw column names, camelCase preference keys) aren't
// fit to show a non-technical admin - maps every field ever passed to `changes` (see the
// `fields:` arrays in attendance.service.ts / shift.service.ts / organization-preferences.service.ts)
// to a plain-English label.
const CHANGE_FIELD_LABELS: Record<string, string> = {
    type: "Type",
    checkin_time: "Check-in Time",
    checkout_time: "Check-out Time",
    dayType: "Day Type",
    mode: "Mode",
    description: "Description",
    status: "Status",
    approvalDescription: "Approval Note",
    name: "Name",
    inTime: "In Time",
    outTime: "Out Time",
    firstHalfOutTime: "First Half Out Time",
    secondHalfInTime: "Second Half In Time",
    tagline: "Tagline",
    updateweekend: "Weekend Days",
    updateovertime: "Standard Working Hours",
    isMarkAbsent: "Auto Mark Absent",
    faceVerification: "Face Verification",
    approvals: "Approval Workflow",
    enabled: "Geofencing",
    locationCount: "Geofence Locations",
};

// Raw enum-ish values that read as codes, not words.
const CHANGE_VALUE_LABELS: Record<string, string> = {
    first_half: "First Half",
    second_half: "Second Half",
    full: "Full Day",
    office: "Office",
    home: "Home",
    remote: "Remote",
    PENDING: "Pending",
    APPROVED: "Approved",
    REJECTED: "Rejected",
};

const CHANGE_TIME_FIELDS = new Set(["checkin_time", "checkout_time"]);

function formatChangeValue(field: string, value: any): string {
    if (value === null || value === undefined || value === "") return "—";
    if (CHANGE_TIME_FIELDS.has(field)) {
        const m = moment(value);
        if (m.isValid()) return m.format("DD-MM-YYYY hh:mm A");
    }
    if (typeof value === "boolean") return value ? "Yes" : "No";
    if (CHANGE_VALUE_LABELS[value]) return CHANGE_VALUE_LABELS[value];
    // Standard Working Hours are stored as dropdown options ({ hours: { label, value }, minutes: ... }).
    if (field === "updateovertime" && typeof value === "object") {
        const part = (p: any) => String(p?.value ?? p ?? 0).padStart(2, "0");
        return `${part(value.hours)}:${part(value.minutes)} hrs`;
    }
    // Lists of options (e.g. Weekend Days) read as their labels.
    if (Array.isArray(value)) {
        return value.length ? value.map((v: any) => v?.label ?? v?.value ?? String(v)).join(", ") : "—";
    }
    if (typeof value === "object") return JSON.stringify(value);
    return String(value);
}

function ActionCell({ action }: { action: string }) {
    const style = ACTION_STYLE[action] || { color: "#64748b", label: action };
    return (
        <Box display="flex" alignItems="center" gap={1}>
            <Box sx={{ width: 7, height: 7, borderRadius: "50%", bgcolor: style.color, flexShrink: 0 }} />
            <Typography sx={{ fontWeight: 600, fontSize: 13 }}>{style.label}</Typography>
        </Box>
    );
}

interface MultiSelectOption {
    id: string | number;
    label: string;
}

// Checkbox multi-select with a "Select All" pseudo-option, matching the pattern already used
// elsewhere in this app (e.g. daily-attendance-report.tsx, AddGstRecord.tsx). An empty
// `selected` means "no filter applied" (i.e. All) - never auto-populated to the full option
// list, so the default stays ALL rather than "every option individually checked".
function MultiSelectFilter({
    label, options, selected, onChange, width = 360,
}: {
    label: string;
    options: MultiSelectOption[];
    selected: Array<string | number>;
    onChange: (ids: Array<string | number>) => void;
    width?: number;
}) {
    const isAllSelected = options.length > 0 && selected.length === options.length;
    const selectAllOption: MultiSelectOption = { id: "__all__", label: "Select All" };

    return (
        <Autocomplete
            multiple
            disableCloseOnSelect
            size="small"
            sx={{ width, flex: "0 0 auto" }}
            options={[selectAllOption, ...options]}
            getOptionLabel={(option) => option.label}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            value={options.filter((o) => selected.includes(o.id))}
            onChange={(_, value, _reason, details) => {
                if (details?.option?.id === "__all__") {
                    onChange(isAllSelected ? [] : options.map((o) => o.id));
                    return;
                }
                onChange(value.map((v) => v.id));
            }}
            renderOption={(props, option, { selected: isChecked }) => (
                <li {...props} key={option.id}>
                    <Checkbox
                        checked={option.id === "__all__" ? isAllSelected : isChecked}
                        indeterminate={option.id === "__all__" && selected.length > 0 && !isAllSelected}
                        sx={{ mr: 1 }}
                    />
                    {option.label}
                </li>
            )}
            renderTags={(value) => {
                if (isAllSelected) return "All";
                if (value.length <= 2) return value.map((v) => v.label).join(", ");
                return `${value.slice(0, 2).map((v) => v.label).join(", ")} +${value.length - 2}`;
            }}
            renderInput={(params) => (
                <TextField {...params} label={label} placeholder={selected.length === 0 ? "All" : ""} />
            )}
        />
    );
}

function DetailsCell({ row }: { row: any }) {
    return (
        <Box>
            {row.remarks && <Typography sx={{ fontSize: 12.5, color: "text.secondary" }}>{row.remarks}</Typography>}
            {Array.isArray(row.changes) && row.changes.length > 0 && (
                <Box mt={0.25}>
                    {row.changes.map((c: any, idx: number) => (
                        <Typography key={idx} sx={{ fontSize: 11.5, color: "text.disabled" }}>
                            {CHANGE_FIELD_LABELS[c.field] || c.field}: {formatChangeValue(c.field, c.from)} → {formatChangeValue(c.field, c.to)}
                        </Typography>
                    ))}
                </Box>
            )}
        </Box>
    );
}

function AdminLog() {
    useTitle("Admin Log · Audit Trail");
    const navigate = useNavigate();
    // DateRange (views/dashboard/OrgDashboard/DateRange) already owns the future-date and
    // from<=to constraints (maxDate/minDate on its two pickers) - default to this month's start
    // through today, same as every other page using this component.
    const [dates, setDates] = useState<{ fromDate: Date | null; toDate: Date | null }>({
        fromDate: moment().startOf("month").toDate(),
        toDate: new Date(),
    });
    const [moduleFilters, setModuleFilters] = useState<Array<string | number>>([]);
    const [actionFilters, setActionFilters] = useState<Array<string | number>>([]);
    const [actorIds, setActorIds] = useState<Array<string | number>>([]);
    const [targetUserIds, setTargetUserIds] = useState<Array<string | number>>([]);
    const [page, setPage] = useState(0);
    const [pageCount, setPageCount] = useState(25);

    const { data: usersRes } = useQuery("users", getUsers);
    const users: any[] = usersRes?.data || [];

    const fromDate = dates.fromDate ? moment(dates.fromDate).format("YYYY-MM-DD") : undefined;
    const toDate = dates.toDate ? moment(dates.toDate).format("YYYY-MM-DD") : undefined;

    // Comma-joined rather than sent as an array param - keeps the query string unambiguous
    // across axios/NestJS's default (non-array) query parsing; the backend splits on ",".
    const queryParams = {
        fromDate,
        toDate,
        module: moduleFilters.length ? moduleFilters.join(",") : undefined,
        action: actionFilters.length ? actionFilters.join(",") : undefined,
        actorId: actorIds.length ? actorIds.join(",") : undefined,
        targetUserId: targetUserIds.length ? targetUserIds.join(",") : undefined,
        limit: pageCount,
        offset: page * pageCount,
    };

    const { data, isLoading }: ResType = useQuery(["admin-activity-log", queryParams], getAdminActivityLog);
    const { data: statsRes }: ResType = useQuery(["admin-activity-log-stats", { fromDate, toDate }], getAdminActivityLogStats);

    const rows: any[] = data?.data?.result || [];
    const totalCount: number = data?.data?.count || 0;
    const stats = statsRes?.data || {};

    const statCards = [
        { label: "Actions Today", value: stats.actionsToday ?? 0 },
        { label: "Approvals (Last 7 Days)", value: stats.approvalsLast7Days ?? 0, color: "#15803d" },
        { label: "Pending Approvals (Last 7 Days)", value: stats.pendingApprovalsLast7Days ?? 0, color: "#7c3aed" },
        { label: "Manual Overrides (Last 7 Days)", value: stats.manualOverrides ?? 0, color: "#d97706" },
        { label: "Settings Changed (Last 7 Days)", value: stats.settingsChanged ?? 0 },
    ];

    const { mutate: exportMutate, isLoading: exporting } = useMutation(exportAdminActivityLog, {
        onSuccess: (res: any) => {
            const blob = new Blob([res.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.download = `ATOM - Admin Activity Log ${getCurrentFormattedDateTime()}.xlsx`;
            link.click();
            snack.success("Exported successfully");
        },
        onError: (err: any) => snack.error(handleError(err)),
    });

    const handleExport = () => {
        if (exporting) return;
        exportMutate({
            fromDate,
            toDate,
            module: moduleFilters.length ? moduleFilters.join(",") : undefined,
            action: actionFilters.length ? actionFilters.join(",") : undefined,
            actorId: actorIds.length ? actorIds.join(",") : undefined,
            targetUserId: targetUserIds.length ? targetUserIds.join(",") : undefined,
        });
    };

    return (
        <Box p={2} sx={{ background: "#f9f9f9" }}>
            <Box display="flex" alignItems="center" gap={1} mb={2}>
                <Button
                    onClick={() => navigate(-1)}
                    startIcon={<ArrowBackIcon fontSize="small" />}
                    size="small"
                    sx={{
                        bgcolor: "#EEF1F5", color: "rgba(0,0,0,0.7)", textTransform: "none",
                        borderRadius: "10px", px: 1.5, py: 0.5, fontWeight: 600, fontSize: 13,
                        minWidth: "auto", boxShadow: "none", "&:hover": { bgcolor: "#E3E7ED", boxShadow: "none" },
                    }}
                >
                    Back
                </Button>
                <Typography sx={{ fontSize: 18, fontWeight: 700 }}>Attendance · Admin Log</Typography>
            </Box>

            {/* <Box
                sx={{
                    display: "flex", gap: 1.5, alignItems: "flex-start",
                    bgcolor: "#fee2e2", border: "1px solid #fecaca", borderRadius: 2, p: 2, mb: 2,
                }}
            >
                <LockOutlinedIcon sx={{ color: "#b91c1c", mt: 0.3 }} fontSize="small" />
                <Box>
                    <Typography sx={{ fontWeight: 700, color: "#b91c1c", fontSize: 14 }}>Admin only</Typography>
                    <Typography sx={{ color: "#b91c1c", fontSize: 12.5 }}>
                        A record of every administrative action taken across the team. Nothing here can be edited or deleted.
                    </Typography>
                </Box>
            </Box> */}

            <Box display="grid" gridTemplateColumns={{ xs: "1fr 1fr", sm: "repeat(5, 1fr)" }} gap={2} mb={2}>
                {statCards.map((card) => (
                    <Box key={card.label} sx={{ border: "1px solid #e3e3e3", borderRadius: 2, p: 2, textAlign: "center", bgcolor: "#fff" }}>
                        <Typography sx={{ fontSize: 26, fontWeight: 700, color: card.color || "#1a2340" }}>{card.value}</Typography>
                        <Typography sx={{ fontSize: 10.5, fontWeight: 600, color: "text.secondary", textTransform: "uppercase", letterSpacing: 0.5, mt: 0.5 }}>
                            {card.label}
                        </Typography>
                    </Box>
                ))}
            </Box>

            <Box display="flex" alignItems="center" gap={1.5} flexWrap="wrap" mb={1.5}>
                <DateRange dates={dates} setDates={setDates} setPage={setPage} />

                <MultiSelectFilter
                    label="Performed By"
                    options={users.map((u: any) => ({ id: u.id, label: u.fullName }))}
                    selected={actorIds}
                    onChange={(ids) => { setActorIds(ids); setPage(0); }}
                    width={250}
                />

                <MultiSelectFilter
                    label="Applies To"
                    options={users.map((u: any) => ({ id: u.id, label: u.fullName }))}
                    selected={targetUserIds}
                    onChange={(ids) => { setTargetUserIds(ids); setPage(0); }}
                    width={250}
                />

                <MultiSelectFilter
                    label="Module"
                    options={MODULE_OPTIONS.filter((opt) => opt.value !== "").map((opt) => ({ id: opt.value, label: opt.label }))}
                    selected={moduleFilters}
                    onChange={(ids) => { setModuleFilters(ids); setPage(0); }}
                    width={180}
                />

                <MultiSelectFilter
                    label="Action"
                    options={Object.entries(ACTION_STYLE).map(([value, style]) => ({ id: value, label: style.label }))}
                    selected={actionFilters}
                    onChange={(ids) => { setActionFilters(ids); setPage(0); }}
                    width={180}
                />

                <Box sx={{ ml: "auto" }}>
                    <Tooltip title={exporting ? "Exporting..." : "Export to Excel"}>
                        <Box
                            component="img"
                            src={excelIcon}
                            alt="Export"
                            onClick={handleExport}
                            sx={{ width: 30, height: 30, cursor: exporting ? "not-allowed" : "pointer", opacity: exporting ? 0.5 : 1 }}
                        />
                    </Tooltip>
                </Box>
            </Box>

            <Box sx={{ border: "1px solid #e3e3e3", borderRadius: "12px", bgcolor: "#fff", overflow: "hidden" }}>
                <Box sx={{ overflowX: "auto" }}>
                    <table style={{ borderCollapse: "collapse", width: "100%" }}>
                        <thead>
                            <tr>
                                {["Date & Time", "Performed By", "Applies To",  "Module", "Action","Details"].map((h) => (
                                    <th
                                        key={h}
                                        style={{
                                            textAlign: "left", padding: "10px 12px", fontSize: 11, fontWeight: 700,
                                            color: "#51607a", textTransform: "uppercase", letterSpacing: 0.5,
                                            borderBottom: "1px solid #e3e8f0", background: "#f8fafc", whiteSpace: "nowrap",
                                        }}
                                    >
                                        {h}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {isLoading ? (
                                <tr><td colSpan={6} style={{ padding: 30, textAlign: "center", color: "#8593a8" }}>Loading…</td></tr>
                            ) : rows.length === 0 ? (
                                <tr><td colSpan={6} style={{ padding: 30, textAlign: "center", color: "#8593a8" }}>No admin activity for this period.</td></tr>
                            ) : (
                                rows.map((row) => (
                                    <tr key={row.id} style={{ borderBottom: "1px solid #f0f0f0" }}>
                                        <td style={{ padding: "10px 12px", fontSize: 12.5, whiteSpace: "nowrap" }}>
                                            {moment(row.createdAt).format("DD MMM, hh:mm A")}
                                        </td>
                                        <td style={{ padding: "10px 12px", fontSize: 12.5 }}>{row.actorName}</td>
                                        <td style={{ padding: "10px 12px", fontSize: 12.5 }}>{row.targetName || "Organization-wide"}</td>
                                        <td style={{ padding: "10px 12px", fontSize: 12.5 }}>{MODULE_LABEL[row.module] || row.module}</td>
                                        <td style={{ padding: "10px 12px" }}><ActionCell action={row.action} /></td>
                                        <td style={{ padding: "10px 12px" }}><DetailsCell row={row} /></td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </Box>
                <TablePagination
                    component="div"
                    sx={{ borderTop: "1px solid #e3e8f0" }}
                    count={totalCount}
                    page={page}
                    onPageChange={(_, newPage) => setPage(newPage)}
                    rowsPerPage={pageCount}
                    onRowsPerPageChange={(e) => { setPageCount(parseInt(e.target.value, 10)); setPage(0); }}
                    rowsPerPageOptions={[10, 25, 50, 100]}
                />
            </Box>
        </Box>
    );
}

export default AdminLog;
